// Campaigns = a workflow + an audience + the numbers it may use + a cap per number per day (SPEC §8). Made by one
// person, approved by a second, then started. It runs one workflow instance ("run") per person; the results are
// counted per campaign, per number and per person (SPEC §10). C-F6: instead of an audience, a campaign can run on a
// schedule: every day (or once) at an hour, on today's cut of a batch or a group (plan v2 §4.4). The schedule is
// approved once; changing it needs a new approval. Each daily run still passes every check of a normal run.
import { z } from 'zod'
import { Channel, Id, IsoDateTime, LeadCode } from './common'
import type { CampaignRunsPage, CampaignRunsQuery } from './campaign-runs'
import type { CampaignWebhooksPage, CampaignWebhooksQuery } from './campaign-webhooks'
import { NumberStatus, Quality } from './numbers'

export const CampaignKind = z.enum(['consent', 'marketing', 'service'])
export type CampaignKind = z.infer<typeof CampaignKind>

/**
 * Consent and marketing campaigns reach people who did not ask us to call: promotional. An RBI-regulated company
 * makes promotional calls only from 140-series numbers (plan v2 §1), so such a campaign calls only from 140.
 */
export function isPromotional(kind: CampaignKind): boolean {
  return kind !== CampaignKind.enum.service
}

/** W1: a draft is still being made in the wizard; nobody else acts on it until it is sent for approval. */
export const CampaignStatus = z.enum([
  'draft',
  'waiting_approval',
  'approved',
  'sending',
  'paused',
  'done',
  'stopped',
])
export type CampaignStatus = z.infer<typeof CampaignStatus>

export const CampaignNumbers = z.object({
  whatsapp: z.array(Id),
  ivr: z.array(Id),
  sms: z.array(Id),
})
export type CampaignNumbers = z.infer<typeof CampaignNumbers>

/**
 * CW1: a number's own daily limit in this campaign (number id → sends a day). A number without one uses the
 * campaign's cap per number; either way never more than the number's own limit (Meta tier, calls a day).
 */
export const NumberCaps = z.record(
  Id,
  z.number('Type a number.').int().min(1, 'A limit is at least 1 a day.')
)
export type NumberCaps = z.infer<typeof NumberCaps>

/** The daily limit a campaign gives one of its numbers: its own, else the campaign cap; never above the number's. */
export function numberCap(
  caps: NumberCaps | undefined,
  capPerNumber: number,
  numberId: string,
  dailyLimit: number
): number {
  return Math.min(dailyLimit, caps?.[numberId] ?? capPerNumber)
}

export const ScheduleSource = z.enum(['batch', 'group'])
export type ScheduleSource = z.infer<typeof ScheduleSource>
export const ScheduleRepeat = z.enum(['daily', 'once'])
export type ScheduleRepeat = z.infer<typeof ScheduleRepeat>
export const SCHEDULE_HOUR_DEFAULT = 10

export const CampaignSchedule = z.object({
  source: ScheduleSource,
  source_id: Id.describe('the batch or group'),
  repeat: ScheduleRepeat,
  hour: z.number('Type an hour.').int().min(0).max(23).describe("IST; runs on the day's cut made before it"),
})
export type CampaignSchedule = z.infer<typeof CampaignSchedule>

const count = z.number().int().min(0)
export const CampaignStats = z.object({
  runs: count,
  excluded: count,
  ivr_calls: count,
  ivr_yes: count,
  ivr_stop: count,
  api_calls: count,
  wa_sent: count,
  wa_delivered: count,
  wa_read: count,
  wa_clicked: count,
  wa_failed: count,
  wa_stop: count,
  wa_block: count,
  sms_sent: count,
  applied: count,
})
export type CampaignStats = z.infer<typeof CampaignStats>

export const CampaignSummary = z.object({
  id: Id,
  name: z.string(),
  kind: CampaignKind,
  workflow_id: Id.nullable().describe('W1: null when the campaign runs its own canvas (made in the wizard)'),
  workflow_name: z.string().describe('the workflow; "<workflow> (fixed copy)" (PD59); or "Own canvas"'),
  audience_id: Id.nullable().describe('null for a scheduled campaign'),
  audience_name: z.string().describe('the audience, or the batch / group of the schedule'),
  schedule: CampaignSchedule.nullable(),
  numbers: CampaignNumbers,
  status: CampaignStatus,
  pause_note: z.string(),
  stats: CampaignStats.nullable().describe('null until the campaign starts'),
  made_by: Id,
  made_by_name: z.string(),
  approved_by_name: z.string().nullable(),
  created_at: IsoDateTime,
  start_at: IsoDateTime.nullable().describe(
    'W1: starts by itself then (now = 5 min after approval, or later)'
  ),
})
export type CampaignSummary = z.infer<typeof CampaignSummary>

export const NumberUse = z.object({
  number_id: Id,
  display: z.string(),
  channel: Channel,
  status: NumberStatus,
  quality: Quality,
  daily_limit: count,
  cap: count.describe(
    "this campaign's daily limit for the number: its own, else the cap per number (never above daily_limit)"
  ),
  own_cap: count
    .nullable()
    .describe('the limit set for this number in this campaign; null = the cap per number'),
  used: count,
  delivered: count.describe('WhatsApp / SMS delivered, IVR answered'),
  blocks: count,
  stops: count,
})
export type NumberUse = z.infer<typeof NumberUse>

export const RunStatus = z.enum(['running', 'waiting', 'done'])
export type RunStatus = z.infer<typeof RunStatus>

export const RunSummary = z.object({
  id: Id,
  lead_code: LeadCode,
  status: RunStatus,
  outcome: z.string(),
  steps: count,
})
export type RunSummary = z.infer<typeof RunSummary>

export const TraceStep = z.object({ at: IsoDateTime, node: z.string(), text: z.string() })
export type TraceStep = z.infer<typeof TraceStep>

export const RunDetail = RunSummary.extend({ campaign_id: Id, trace: z.array(TraceStep) })
export type RunDetail = z.infer<typeof RunDetail>

export const DailyRun = z.object({
  id: Id,
  date: z.string().describe('IST date'),
  cut_count: count.describe("people in that day's cut"),
  started: count.describe('runs started'),
  excluded: count.describe('left out: suppression list or the marketing gap'),
})
export type DailyRun = z.infer<typeof DailyRun>

export const CampaignDetail = CampaignSummary.extend({
  cap_per_number: count,
  number_caps: NumberCaps,
  per_hour: count.describe('runs stepped per hour (demo speed)'),
  version: z.number().int().nullable().describe('the workflow version fixed at the start'),
  started_at: IsoDateTime.nullable(),
  finished_at: IsoDateTime.nullable(),
  still_running: count,
  waiting_note: z
    .string()
    .nullable()
    .describe('W7: what the open runs wait for ("68 waiting for their next step … the first at 17:12"), null when none'),
  per_number: z.array(NumberUse),
  runs: z.array(RunSummary).describe('the first 60'),
  runs_total: count,
  daily_runs: z.array(DailyRun).describe('scheduled campaigns: the newest 30 days it ran'),
  daily_runs_total: count,
})
export type CampaignDetail = z.infer<typeof CampaignDetail>

export const CreateCampaignInput = z
  .object({
    name: z.string().trim().min(3, 'Give the campaign a name'),
    kind: CampaignKind,
    workflow_id: Id,
    audience_id: Id.nullable().describe('null when the campaign runs on a schedule'),
    schedule: CampaignSchedule.nullable().optional(),
    cap_per_number: z.number('Type a number.').int().min(1, 'The cap is at least 1 a day.'),
    numbers: CampaignNumbers,
    number_caps: NumberCaps.optional().describe('CW1: own daily limits of some of the chosen numbers'),
  })
  .refine((input) => !!input.audience_id !== !!input.schedule, {
    path: ['audience_id'],
    message: 'Pick an audience, or a batch / group on a schedule',
  })
  .refine(
    (input) => {
      const chosen = [...input.numbers.whatsapp, ...input.numbers.ivr, ...input.numbers.sms]
      return Object.keys(input.number_caps ?? {}).every((id) => chosen.includes(id))
    },
    { path: ['number_caps'], message: 'A daily limit is set for a number the campaign does not use' }
  )
export type CreateCampaignInput = z.infer<typeof CreateCampaignInput>

/** W5: one status count (the last status of each message, per channel; or an IVR result). */
export const StatusCount = z.object({ status: z.string(), n: count })
export type StatusCount = z.infer<typeof StatusCount>

export const FUNNEL_STEPS = ['people', 'sent', 'delivered', 'read', 'clicked', 'applied'] as const
export const FunnelStep = z.enum(FUNNEL_STEPS)
export type FunnelStep = z.infer<typeof FunnelStep>

/**
 * W5 (owner 2026-10-30: "Exotel's stats on our campaign too"): the Delivery panel of a campaign, read from the events
 * in ClickHouse. `source` none = ClickHouse is not set up or down: the panel says so (the tiles still show the totals).
 */
export const CampaignDelivery = z.object({
  source: z.enum(['clickhouse', 'none']),
  waiting: count.describe('events still in the queue, not yet in ClickHouse'),
  channels: z.array(
    z.object({
      channel: Channel,
      messages: count.describe('messages of this channel (one per person per send)'),
      statuses: z.array(StatusCount).describe('the last status of each message: sent, delivered, read, failed, pressed 1 (yes) …'),
    })
  ),
  blocks: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z.string(),
        entered: count.describe('people who reached this block (left it, or are on it now)'),
        now: count.describe('people on it right now (waiting for a key press, a window, a lender …)'),
        exits: z.array(z.object({ port: z.string(), n: count })).describe('who left by which exit: pass / fail, press_1 / no_answer, link / no_link, next, end'),
        statuses: z.array(StatusCount).describe("what the vendor or lender said there: a message block's last statuses, a lender block's answers"),
      })
    )
    .describe("PD65: the campaign's own blocks in the order they run, block by block (the report follows the workflow)"),
  funnel: z.array(z.object({ step: FunnelStep, n: count })).describe('people → sent → delivered → read → clicked → applied'),
  daily: z
    .array(z.object({ day: z.string(), channel: Channel, status: z.string(), n: count }))
    .describe('the last 31 days, campaign × day × channel × status (every status change counted once)'),
})
export type CampaignDelivery = z.infer<typeof CampaignDelivery>

/** W5: one person of the campaign report (Download report: a CSV made on the screen; no mobile, no name). */
export const CampaignReportRow = z.object({
  lead_code: z.string(),
  channel: z.string().describe('the channel of the last message'),
  status: z.string().describe('the last status of that message'),
  at: IsoDateTime.nullable().describe('null: no message yet (ended at a dedupe, still on the IVR …)'),
  digit: z.string().describe('the IVR key pressed, or empty'),
  lender: z.string(),
  lender_status: z.string(),
  lender_reference: z.string().describe("the lender's own lead id, or empty"),
  got_link: z.boolean(),
  clicked: count.describe('clicks on the short link (link previews not counted)'),
  outcome: z.string().describe('how the run ended, or empty while it runs'),
  steps: z
    .record(z.string(), z.string())
    .describe('PD65: block name → the exit this person took there (one CSV column per block)'),
})
export type CampaignReportRow = z.infer<typeof CampaignReportRow>

export interface CampaignsApi {
  listCampaigns(): Promise<CampaignSummary[]>
  getCampaign(id: string): Promise<CampaignDetail>
  createCampaign(input: CreateCampaignInput): Promise<CampaignSummary>
  /** a second person only, never the maker */
  approveCampaign(id: string): Promise<CampaignSummary>
  startCampaign(id: string): Promise<CampaignSummary>
  pauseCampaign(id: string): Promise<CampaignSummary>
  resumeCampaign(id: string): Promise<CampaignSummary>
  stopCampaign(id: string): Promise<CampaignSummary>
  getRun(id: string): Promise<RunDetail>
  /** W5: the Delivery panel (the events in ClickHouse) */
  getCampaignDelivery(id: string): Promise<CampaignDelivery>
  /** W5: one row per person for Download report (at most 10,000; the screen makes the CSV) */
  getCampaignReport(id: string): Promise<CampaignReportRow[]>
  /** W7: the Runs tab from ClickHouse, 50 a page by a cursor, with filters; limit 5,000 for an export */
  listCampaignRuns(id: string, query: CampaignRunsQuery): Promise<CampaignRunsPage>
  /** WH1: the Webhooks tab, newest first, 50 a page by a cursor, with filters */
  listCampaignWebhooks(id: string, query: CampaignWebhooksQuery): Promise<CampaignWebhooksPage>
  /** WH1: where Download CSV gets every webhook line of the campaign (the filters apply); null = the mock (made on the screen) */
  campaignWebhooksCsvUrl(id: string, query: CampaignWebhooksQuery): string | null
  /** a new schedule: the campaign waits for a second person's approval again, then Start */
  changeCampaignSchedule(id: string, schedule: CampaignSchedule): Promise<CampaignSummary>
}
